// Generated by Claude Code
import { useEffect } from 'react'
import { State, useGlobalState, type PropertySelector } from '#core/state/State.ts'
import LimitedAccessApi, { type EffectiveLimitedAccess } from '#core/api/operations/LimitedAccessApi.ts'
import type { PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useUserState } from '#core/state/UserState.ts'

/**
 * Global in-memory state for the caller's effective limited-access whitelists.
 * Holds, per limited-capable service, which properties the active
 * user may read/update — `null` meaning unrestricted (full READ/UPDATE grant).
 *
 * Consumed by tables, detail views, and edit forms to only render the
 * columns/fields the user is allowed to see. This is a UX filter only — the
 * backend independently enforces the whitelists at serialization time.
 *
 * Use {@link useLimitedAccess} to subscribe; it reloads on company switch.
 */
export class LimitedAccessState extends State<LimitedAccessState> {
    /** Effective access per service, as returned by the backend. */
    effective: EffectiveLimitedAccess[] = [];
    $isLoading: boolean = false;
    $loadedCompanyId: string | null = null;
    /** Company id of the last load *attempt*, success or failure. Underscore-prefixed so
     *  recording an attempt raises no re-render. */
    _attemptedCompanyId: string | null = null;

    private readonly _api = new LimitedAccessApi();

    /**
     * Loads the effective whitelists for the given company when not already
     * attempted (or when the active company changed). Safe to call repeatedly.
     * A failed attempt is not retried until {@link reload} or {@link invalidate}.
     */
    ensureLoaded(companyId: string): void {
        if (this.$isLoading || this._attemptedCompanyId === companyId) return;
        void this._load(companyId);
    }

    /** Forces a reload (e.g. after the company's limited-access config was edited). */
    reload(): void {
        const companyId = this.$loadedCompanyId ?? this._attemptedCompanyId;
        if (companyId) void this._load(companyId);
    }

    /** Clears the load markers so the next {@link ensureLoaded} refetches. */
    invalidate(): void {
        this.reactTo.$loadedCompanyId = null;
        this.reactTo._attemptedCompanyId = null;
    }

    /**
     * Returns the readable property whitelist for a service, or `null` when
     * the user has unrestricted read access (or data has not loaded yet —
     * fail-open here is safe because the backend enforces the whitelist).
     */
    allowedReadProperties(service: PermissionService): string[] | null {
        return this.effective.find((e) => e.service === service)?.read_properties ?? null;
    }

    /**
     * Returns the writable property whitelist for a service, or `null` when
     * the user has unrestricted update access.
     */
    allowedUpdateProperties(service: PermissionService): string[] | null {
        return this.effective.find((e) => e.service === service)?.update_properties ?? null;
    }

    /**
     * Returns whether a single property is readable for the user on a service.
     * Properties with dotted paths (e.g. `custom_properties.foo`) are matched
     * on their root segment, mirroring how the backend filters whole fields.
     */
    canReadProperty(service: PermissionService, property: string): boolean {
        const allowed = this.allowedReadProperties(service);
        if (allowed === null) return true;
        const root = property.split('.')[0];
        return allowed.includes(property) || allowed.includes(root);
    }

    private async _load(companyId: string): Promise<void> {
        this.reactTo.$isLoading = true;
        // Marked before the request, not after: `Api.get` throws on a non-2xx, and clearing
        // $isLoading re-runs every subscriber's effect, which calls ensureLoaded again. Without
        // the marker already standing a failure retries at network speed for the tab's lifetime.
        this.reactTo._attemptedCompanyId = companyId;
        try {
            const result = await this._api.getEffective();
            if (result) {
                this.reactTo.effective = result;
                this.reactTo.$loadedCompanyId = companyId;
            }
        } catch {
            // Fail open: the whitelist is a rendering filter and the backend omits the data
            // independently, so an unresolved whitelist costs no safety.
        } finally {
            this.reactTo.$isLoading = false;
        }
    }
}

/**
 * Hook exposing the {@link LimitedAccessState}, auto-loading the effective
 * whitelists for the active company once the user is logged in.
 */
export function useLimitedAccess(filter: PropertySelector<LimitedAccessState> | undefined = undefined) {
    const userState = useUserState();
    const state = useGlobalState(LimitedAccessState, filter);
    useEffect(() => {
        if (userState.loginState === 'failed') {
            state.invalidate();
        } else if (userState.loginState === 'success' && userState.activeCompanyId) {
            state.ensureLoaded(userState.activeCompanyId);
        }
        // `state` is out of the deps on purpose: every property change hands the hook a new
        // proxy, so depending on it re-runs this effect on every state change. The instance is
        // a StateView singleton and is never swapped.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState.loginState, userState.activeCompanyId]);
    return state;
}
